<template>
  <div class="cabadmin-users">
    <!-- Actions bar -->
    <div class="actions-bar">
      <div class="filters">
        <input
          v-model="search"
          type="text"
          class="form-input"
          placeholder="Rechercher un email…"
          @input="debouncedReload"
        />
      </div>
      <div class="actions">
        <button class="btn btn-outline" @click="showBulkModal = true">Import CSV…</button>
        <button class="btn btn-primary" @click="showAddModal = true">+ Ajouter un utilisateur</button>
      </div>
    </div>

    <!-- Table -->
    <div class="table-wrap">
      <table class="users-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Ajouté le</th>
            <th class="col-actions"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading"><td colspan="3" class="state-row">Chargement…</td></tr>
          <tr v-else-if="!users.length"><td colspan="3" class="state-row">Aucun utilisateur.</td></tr>
          <tr v-for="u in users" :key="u.allowlist_id">
            <td>
              <span class="user-email">{{ u.email }}</span>
              <span v-if="u.is_cab_admin" class="admin-pill">admin</span>
            </td>
            <td class="muted">{{ formatDate(u.added_at) }}</td>
            <td class="col-actions">
              <button class="btn-link danger" @click="onRemove(u)" title="Retirer">Retirer</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal : ajout d'un email -->
    <div v-if="showAddModal" class="modal-overlay" @click.self="closeAddModal">
      <div class="modal-card">
        <h3>Ajouter un utilisateur</h3>
        <p class="modal-help">Cet email sera autorisé à se connecter au cabinet. Pensez à prévenir l'utilisateur — aucun mail n'est envoyé automatiquement.</p>
        <form @submit.prevent="onAdd">
          <label class="field-label">Email</label>
          <input
            v-model="addEmail"
            type="email"
            class="form-input"
            placeholder="prenom.nom@cabinet.com"
            required
            autofocus
          />
          <label class="field-label">Note (optionnel)</label>
          <input v-model="addNote" type="text" class="form-input" placeholder="Bureau Paris, équipe contentieux…" />
          <div v-if="addError" class="modal-error">{{ addError }}</div>
          <div class="modal-actions">
            <button type="button" class="btn btn-outline" @click="closeAddModal">Annuler</button>
            <button type="submit" class="btn btn-primary" :disabled="addBusy">
              {{ addBusy ? 'Ajout…' : 'Ajouter' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal : bulk import CSV -->
    <div v-if="showBulkModal" class="modal-overlay" @click.self="closeBulkModal">
      <div class="modal-card modal-large">
        <h3>Import en masse</h3>
        <p class="modal-help">Collez un email par ligne. Les doublons et formats invalides seront ignorés. Si vous dépassez le nombre de sièges restants, seuls les premiers seront ajoutés.</p>
        <textarea
          v-model="bulkRaw"
          class="form-input bulk-textarea"
          rows="10"
          placeholder="alice@cabinet.com&#10;bob@cabinet.com&#10;…"
        ></textarea>
        <div class="bulk-summary">{{ bulkEmails.length }} email{{ bulkEmails.length > 1 ? 's' : '' }} détecté{{ bulkEmails.length > 1 ? 's' : '' }}</div>
        <div v-if="bulkResult" class="bulk-result">
          <div><strong>{{ bulkResult.added.length }}</strong> ajouté{{ bulkResult.added.length > 1 ? 's' : '' }}.</div>
          <div v-if="bulkResult.skipped.length"><strong>{{ bulkResult.skipped.length }}</strong> ignoré{{ bulkResult.skipped.length > 1 ? 's' : '' }} :</div>
          <ul v-if="bulkResult.skipped.length" class="skipped-list">
            <li v-for="(s, i) in bulkResult.skipped" :key="i">
              <code>{{ s.email }}</code> — {{ reasonLabel(s.reason) }}
            </li>
          </ul>
        </div>
        <div v-if="bulkError" class="modal-error">{{ bulkError }}</div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" @click="closeBulkModal">Fermer</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="bulkBusy || !bulkEmails.length"
            @click="onBulkSubmit"
          >{{ bulkBusy ? 'Import…' : `Importer ${bulkEmails.length}` }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { listUsers, addEmail, bulkAddEmails, removeEntry } from '@/services/cabadmin';

const REASON_LABELS = {
  invalid_email: 'Format email invalide',
  duplicate_in_payload: 'Doublon dans votre liste',
  duplicate: 'Déjà présent dans ce cabinet',
  email_in_other_cabinet: 'Déjà actif dans un autre cabinet',
  seats_full: 'Plus de sièges disponibles',
  insert_failed: "Échec d'insertion",
  error: 'Erreur serveur'
};

export default {
  name: 'CabAdminUsers',
  props: {
    cabinet: { type: Object, default: null }
  },
  data() {
    return {
      users: [],
      loading: false,
      search: '',
      searchTimer: null,
      showAddModal: false,
      addEmail: '',
      addNote: '',
      addBusy: false,
      addError: null,
      showBulkModal: false,
      bulkRaw: '',
      bulkBusy: false,
      bulkError: null,
      bulkResult: null
    };
  },
  computed: {
    bulkEmails() {
      return this.bulkRaw
        .split(/[\s,;]+/)
        .map(s => s.trim().toLowerCase())
        .filter(Boolean);
    }
  },
  async mounted() {
    await this.reload();
  },
  methods: {
    formatDate(iso) {
      if (!iso) return '';
      try {
        return new Date(iso).toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (_) {
        return iso;
      }
    },
    reasonLabel(r) {
      return REASON_LABELS[r] || r;
    },
    debouncedReload() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.reload(), 250);
    },
    async reload() {
      this.loading = true;
      try {
        this.users = await listUsers({ search: this.search });
      } catch (e) {
        console.error('[CabAdminUsers] reload failed:', e);
      } finally {
        this.loading = false;
      }
    },
    closeAddModal() {
      this.showAddModal = false;
      this.addEmail = '';
      this.addNote = '';
      this.addError = null;
    },
    async onAdd() {
      this.addBusy = true;
      this.addError = null;
      try {
        await addEmail(this.addEmail.trim().toLowerCase(), this.addNote.trim() || null);
        this.closeAddModal();
        this.$emit('refresh');
        await this.reload();
      } catch (e) {
        const code = e.response?.data?.error;
        if (code === 'seats_full') {
          this.addError = 'Plus de sièges disponibles. Retirez un utilisateur ou augmentez votre abonnement.';
        } else if (code === 'email_in_other_cabinet') {
          this.addError = 'Cet email est déjà actif dans un autre cabinet. Contactez Query Juriste.';
        } else if (code === 'duplicate') {
          this.addError = 'Cet email est déjà dans votre cabinet.';
        } else if (code === 'invalid_email') {
          this.addError = "Format d'email invalide.";
        } else {
          this.addError = "Erreur. Réessayez ou contactez Query Juriste.";
        }
      } finally {
        this.addBusy = false;
      }
    },
    closeBulkModal() {
      this.showBulkModal = false;
      this.bulkRaw = '';
      this.bulkResult = null;
      this.bulkError = null;
    },
    async onBulkSubmit() {
      this.bulkBusy = true;
      this.bulkError = null;
      this.bulkResult = null;
      try {
        this.bulkResult = await bulkAddEmails(this.bulkEmails);
        this.$emit('refresh');
        await this.reload();
      } catch (e) {
        console.error('[CabAdminUsers] bulk failed:', e);
        this.bulkError = "Erreur durant l'import. Réessayez.";
      } finally {
        this.bulkBusy = false;
      }
    },
    async onRemove(user) {
      const msg = `Retirer ${user.email} ?\n\nIl perdra immédiatement l'accès au cabinet. Son siège sera libéré.`;
      if (!window.confirm(msg)) return;
      try {
        await removeEntry(user.allowlist_id);
        this.$emit('refresh');
        await this.reload();
      } catch (e) {
        console.error('[CabAdminUsers] remove failed:', e);
        alert("Impossible de retirer cet utilisateur.");
      }
    }
  }
};
</script>

<style scoped>
.actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.filters { display: flex; gap: 8px; flex: 1 1 auto; min-width: 240px; max-width: 480px; }
.actions { display: flex; gap: 8px; }
.form-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  flex: 1 1 auto;
}
.btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary { background: #6c63ff; color: white; }
.btn-primary:hover:not(:disabled) { background: #5a54d1; }
.btn-outline { background: white; border: 1px solid #ddd; color: #555; }
.btn-outline:hover { border-color: #999; color: #222; }

.table-wrap {
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
}
.users-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.users-table th, .users-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
}
.users-table th { background: #fafafa; font-weight: 600; color: #555; font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em; }
.users-table tbody tr:hover { background: #fafbff; }
.users-table tbody tr:last-child td { border-bottom: none; }
.user-email { font-weight: 500; }
.admin-pill {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  background: #ede9fe;
  color: #6c63ff;
  font-size: 0.75em;
  border-radius: 10px;
  font-weight: 500;
}
.muted { color: #777; }
.col-actions { text-align: right; white-space: nowrap; }
.btn-link {
  background: none;
  border: none;
  color: #6c63ff;
  cursor: pointer;
  font-size: 13px;
  padding: 4px 8px;
  border-radius: 4px;
}
.btn-link:hover { background: #f5f3ff; }
.btn-link.danger { color: #c62828; }
.btn-link.danger:hover { background: #fff5f5; }
.state-row { text-align: center; padding: 32px 16px; color: #999; }

.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
}
.modal-card {
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 100%; max-width: 440px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.2);
}
.modal-large { max-width: 600px; }
.modal-card h3 { margin: 0 0 8px; font-size: 18px; }
.modal-help { color: #666; font-size: 13px; margin: 0 0 16px; }
.field-label { display: block; font-size: 12px; color: #555; margin: 12px 0 4px; }
.bulk-textarea { width: 100%; font-family: monospace; font-size: 13px; }
.bulk-summary { margin-top: 8px; font-size: 13px; color: #666; }
.bulk-result { margin-top: 16px; padding: 12px; background: #f5f5ff; border-radius: 6px; font-size: 13px; }
.skipped-list { margin: 8px 0 0; padding-left: 20px; }
.skipped-list code { background: #fff; padding: 1px 4px; border-radius: 3px; }
.modal-error {
  margin-top: 12px;
  padding: 10px 12px;
  background: #fff5f5;
  border: 1px solid #ffcdd2;
  border-radius: 6px;
  color: #c62828;
  font-size: 13px;
}
.modal-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 20px; }
</style>
